import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { ROUTES } from "../../core/routes/routes";
import { DateUtil } from "../../common/utils/date.util";
import { useGroupedFunctionalityIndicatorsQuery } from "./hooks/useGroupedFunctionalityIndicatorsQuery.js";
import { useFunctionalityIndicatorsCommand } from "./hooks/useFunctionalityIndicatorsCommand.js";
import { exportFunctionalityIndicatorsToExcelService } from "./services/exportFunctionalityIndicatorsToExcel.service.js";
import { useFetch } from "../../common/hooks/useFetch";
import { saveFile } from "../../common/utils/saveFile.util";
import IconButton from "../../common/components/IconButton";
import Textfield from "../../common/components/Textfiled";
import { BsCalendarDay, BsCalendarMonth, BsFileSpreadsheet, BsPencilSquare, BsTrash } from "react-icons/bs";
import Swal from "sweetalert2";

const MONTH_NAMES = [
  "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
  "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre",
];

function formatMonth(monthKey) {
  const [year, month] = monthKey.split("-");
  return `${MONTH_NAMES[parseInt(month, 10) - 1]} ${year}`;
}

/** Convierte valores de celda a número seguro (evita NaN con "-", null, etc.). */
function toNumber(value) {
  if (value === null || value === undefined || value === "" || value === "-") {
    return 0;
  }
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

function ListFunctionalityIndicators() {
  const navigate = useNavigate();
  const fetchData = useFetch();
  const [filters, setFilters] = useState({
    startDate: "",
    endDate: "",
  });
  const { groupedData, loading, refetch } =
    useGroupedFunctionalityIndicatorsQuery(filters);
  const {
    indicators: editForm,
    handleChange: handleEditChange,
    loadIndicator,
    updateIndicator,
    deleteIndicator,
    errors: { errors: editErrors },
    loading: commandLoading,
  } = useFunctionalityIndicatorsCommand();

  const [editModal, setEditModal] = useState({ open: false, type: null });

  const openEditModal = (record, type) => {
    loadIndicator(record);
    setEditModal({ open: true, type });
  };

  const closeEditModal = () => {
    setEditModal({ open: false, type: null });
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    try {
      await updateIndicator({ ...editForm, type: editModal.type });
      closeEditModal();
      refetch();
    } catch (error) {
      // error already handled in hook
    }
  };

  const handleDelete = async (id) => {
    const deleted = await deleteIndicator(id);
    if (deleted) {
      refetch();
    }
  };

  const exportToExcel = async () => {
    try {
      const response = await exportFunctionalityIndicatorsToExcelService(fetchData)(filters);
      saveFile(response, "indicadores_funcionamiento.xlsx");
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "No se pudo exportar a Excel",
      });
    }
  };

  const tableRows = useMemo(() => {
    const rows = [];

    for (const group of groupedData) {
      const dailyRows = group.daily || [];
      const monthly = group.monthly || null;
      const dataRowCount = Math.max(dailyRows.length, 1);
      // Mes abarca filas de datos + fila Total
      const monthRowSpan = dataRowCount + 1;
      // Columnas mensuales solo abarcan filas de datos (no el Total)
      const monthlyRowSpan = dataRowCount;

      const sumExcesses = dailyRows.reduce(
        (sum, d) => sum + toNumber(d.dailyWorkShiftExcesses),
        0
      );
      const sumSpeeding = dailyRows.reduce(
        (sum, d) => sum + toNumber(d.dailySpeedingWorkTrips),
        0
      );

      if (dailyRows.length === 0) {
        rows.push({
          isFirst: true,
          isTotal: false,
          monthRowSpan,
          monthlyRowSpan,
          month: formatMonth(group.month),
          date: "-",
          dailyWorkShiftExcesses: "-",
          dailySpeedingWorkTrips: "-",
          totalDriverWorkDays: monthly ? monthly.totalDriverWorkDays : "-",
          totalWorkTrips: monthly ? monthly.totalWorkTrips : "-",
          dailyRecord: null,
          monthlyRecord: monthly,
        });
      } else {
        dailyRows.forEach((d, i) => {
          rows.push({
            isFirst: i === 0,
            isTotal: false,
            monthRowSpan,
            monthlyRowSpan,
            month: formatMonth(group.month),
            date: DateUtil.format(d.date, "DD/MM/YYYY"),
            dailyWorkShiftExcesses: d.dailyWorkShiftExcesses,
            dailySpeedingWorkTrips: d.dailySpeedingWorkTrips,
            totalDriverWorkDays: monthly ? monthly.totalDriverWorkDays : "-",
            totalWorkTrips: monthly ? monthly.totalWorkTrips : "-",
            dailyRecord: d,
            monthlyRecord: monthly,
          });
        });
      }

      rows.push({
        isFirst: false,
        isTotal: true,
        monthRowSpan,
        monthlyRowSpan,
        month: formatMonth(group.month),
        date: "Total",
        dailyWorkShiftExcesses: sumExcesses,
        dailySpeedingWorkTrips: sumSpeeding,
        // Mensual ya se muestra en las filas del mes; en Total se deja en blanco
        totalDriverWorkDays: "",
        totalWorkTrips: "",
        dailyRecord: null,
        monthlyRecord: null,
      });
    }

    return rows;
  }, [groupedData]);

  return (
    <main className="container-fluid p-4">
      <style>{`
        .fi-table-wrap {
          overflow-x: auto;
          font-family: Arial, sans-serif;
          max-width: 100%;
        }
        .fi-table {
          width: 100%;
          border-collapse: collapse;
        }
        .fi-table thead tr {
          background: linear-gradient(135deg, #4a6cf7 0%, #6366f1 100%);
        }
        .fi-table th {
          padding: 14px 12px;
          text-align: center;
          font-weight: 600;
          font-size: 13px;
          color: #fff;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          border-right: 1px solid rgba(255,255,255,0.15);
        }
        .fi-table th:last-child {
          border-right: none;
        }
        .fi-table th.fi-col-daily {
          background: linear-gradient(135deg, #059669 0%, #10b981 100%);
        }
        .fi-table th.fi-col-monthly {
          background: linear-gradient(135deg, #d97706 0%, #f59e0b 100%);
        }
        .fi-table td {
          padding: 10px 12px;
          font-size: 13px;
          color: #374151;
          border-bottom: 1px solid #e5e7eb;
          border-right: 1px solid #f3f4f6;
          text-align: center;
        }
        .fi-table tr td:last-child {
          border-right: none;
        }
        .fi-table tbody tr:hover td {
          background-color: #eef2ff;
        }
        .fi-cell-month {
          font-weight: 700;
          font-size: 14px;
          color: #1e293b;
          background-color: #f1f5f9 !important;
          border-right: 2px solid #e2e8f0 !important;
          white-space: nowrap;
        }
        .fi-cell-monthly {
          background-color: #fffbeb !important;
          font-weight: 600;
          font-size: 15px;
          color: #92400e;
          border-right: 1px solid #fde68a !important;
        }
        .fi-cell-monthly:last-child {
          border-right: none !important;
        }
        .fi-cell-daily-val {
          font-weight: 500;
        }
        .fi-loading,
        .fi-empty {
          text-align: center;
          padding: 32px 20px;
          color: #9ca3af;
          font-style: italic;
          font-size: 14px;
        }
        .fi-subheader {
          background-color: #f8fafc;
        }
        .fi-subheader td {
          font-size: 11px;
          color: #9ca3af;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          padding: 6px 12px;
          font-weight: 600;
          border-bottom: 2px solid #e5e7eb;
        }
        .fi-badge-daily {
          display: inline-block;
          background: #d1fae5;
          color: #065f46;
          font-size: 10px;
          font-weight: 700;
          padding: 2px 8px;
          border-radius: 9999px;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }
        .fi-badge-monthly {
          display: inline-block;
          background: #fef3c7;
          color: #92400e;
          font-size: 10px;
          font-weight: 700;
          padding: 2px 8px;
          border-radius: 9999px;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }
        .fi-row-total td {
          font-weight: 700;
          color: #374151;
          border-top: 1px solid #e5e7eb;
        }
        .fi-cell-total-label {
          text-transform: uppercase;
          letter-spacing: 0.4px;
          font-weight: 700;
        }
        @media (max-width: 768px) {
          .fi-table th { font-size: 11px; padding: 10px 6px; }
          .fi-table td { font-size: 12px; padding: 8px 6px; }
        }
      `}</style>

      <header className="d-flex justify-content-between align-items-center mb-4 px-3 py-2 border-bottom">
        <h1 className="h2 m-0 fw-bold">Indicadores de Funcionamiento</h1>
        <div className="d-flex gap-2">
          <IconButton
            tooltipText="Exportar a Excel"
            onClick={exportToExcel}
          >
            <BsFileSpreadsheet size={24} />
          </IconButton>
          <IconButton
            tooltipText="Registrar indicador diario"
            onClick={() =>
              navigate(ROUTES.REGISTER_DAILY_FUNCTIONALITY_INDICATORS)
            }
          >
            <BsCalendarDay size={24} />
          </IconButton>
          <IconButton
            tooltipText="Registrar indicador mensual"
            onClick={() =>
              navigate(ROUTES.REGISTER_MONTHLY_FUNCTIONALITY_INDICATORS)
            }
          >
            <BsCalendarMonth size={24} />
          </IconButton>
        </div>
      </header>

      <section className="shadow-sm rounded w-100 d-flex flex-column" style={{ border: "1px solid #e5e7eb" }}>
        <div className="d-flex gap-2 justify-content-center justify-content-md-end p-3">
          <Textfield
            label="Fecha inicio"
            type="date"
            name="startDate"
            value={filters.startDate}
            onChange={({ value }) =>
              setFilters((prev) => ({ ...prev, startDate: value }))
            }
          />
          <Textfield
            label="Fecha fin"
            type="date"
            name="endDate"
            value={filters.endDate}
            onChange={({ value }) =>
              setFilters((prev) => ({ ...prev, endDate: value }))
            }
          />
        </div>

        <div className="fi-table-wrap">
          <table className="fi-table">
            <thead>
              <tr>
                <th rowSpan={2} style={{ borderBottom: "2px solid rgba(255,255,255,0.3)", minWidth: 120 }}>Mes</th>
                <th rowSpan={2} style={{ borderBottom: "2px solid rgba(255,255,255,0.3)", minWidth: 100 }}>Fecha</th>
                <th colSpan={2} className="fi-col-daily" style={{ borderBottom: "1px solid rgba(255,255,255,0.3)" }}>
                  <span className="fi-badge-daily">Diario</span>
                </th>
                <th rowSpan={2} className="fi-col-daily" style={{ borderBottom: "2px solid rgba(255,255,255,0.3)", minWidth: 90 }}>
                  Acciones Diario
                </th>
                <th colSpan={2} className="fi-col-monthly" style={{ borderBottom: "1px solid rgba(255,255,255,0.3)" }}>
                  <span className="fi-badge-monthly">Mensual</span>
                </th>
                <th rowSpan={2} className="fi-col-monthly" style={{ borderBottom: "2px solid rgba(255,255,255,0.3)", minWidth: 90 }}>
                  Acciones Mensual
                </th>
              </tr>
              <tr>
                <th className="fi-col-daily" style={{ fontSize: 11, fontWeight: 500, padding: "8px 10px" }}>
                  Excesos de turnos
                </th>
                <th className="fi-col-daily" style={{ fontSize: 11, fontWeight: 500, padding: "8px 10px" }}>
                  Viajes con exceso velocidad
                </th>
                <th className="fi-col-monthly" style={{ fontSize: 11, fontWeight: 500, padding: "8px 10px" }}>
                  Días trabajo conductores
                </th>
                <th className="fi-col-monthly" style={{ fontSize: 11, fontWeight: 500, padding: "8px 10px" }}>
                  Total viajes trabajo
                </th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={8} className="fi-loading">
                    Cargando datos...
                  </td>
                </tr>
              ) : tableRows.length === 0 ? (
                <tr>
                  <td colSpan={8} className="fi-empty">
                    No hay datos para mostrar.
                  </td>
                </tr>
              ) : (
                tableRows.map((row, idx) => (
                  <tr key={idx} className={row.isTotal ? "fi-row-total" : undefined}>
                    {row.isFirst && (
                      <td rowSpan={row.monthRowSpan} className="fi-cell-month">
                        {row.month}
                      </td>
                    )}
                    <td className={row.isTotal ? "fi-cell-total-label" : undefined}>
                      {row.date}
                    </td>
                    <td className="fi-cell-daily-val">{row.dailyWorkShiftExcesses}</td>
                    <td className="fi-cell-daily-val">{row.dailySpeedingWorkTrips}</td>
                    <td>
                      {!row.isTotal && row.dailyRecord && (
                        <div className="d-flex gap-1 justify-content-center">
                          <button
                            className="btn btn-sm btn-outline-success"
                            title="Editar diario"
                            onClick={() => openEditModal(row.dailyRecord, "diario")}
                          >
                            <BsPencilSquare />
                          </button>
                          <button
                            className="btn btn-sm btn-outline-danger"
                            title="Eliminar diario"
                            onClick={() => handleDelete(row.dailyRecord.id)}
                          >
                            <BsTrash />
                          </button>
                        </div>
                      )}
                    </td>
                    {row.isFirst && (
                      <>
                        <td rowSpan={row.monthlyRowSpan} className="fi-cell-monthly">
                          {row.totalDriverWorkDays}
                        </td>
                        <td rowSpan={row.monthlyRowSpan} className="fi-cell-monthly">
                          {row.totalWorkTrips}
                        </td>
                        <td rowSpan={row.monthlyRowSpan} className="fi-cell-monthly">
                          {row.monthlyRecord && (
                            <div className="d-flex gap-1 justify-content-center">
                              <button
                                className="btn btn-sm btn-outline-warning"
                                title="Editar mensual"
                                onClick={() => openEditModal(row.monthlyRecord, "mensual")}
                              >
                                <BsPencilSquare />
                              </button>
                              <button
                                className="btn btn-sm btn-outline-danger"
                                title="Eliminar mensual"
                                onClick={() => handleDelete(row.monthlyRecord.id)}
                              >
                                <BsTrash />
                              </button>
                            </div>
                          )}
                        </td>
                      </>
                    )}
                    {row.isTotal && (
                      <>
                        <td className="fi-cell-monthly">{row.totalDriverWorkDays}</td>
                        <td className="fi-cell-monthly">{row.totalWorkTrips}</td>
                        <td className="fi-cell-monthly" />
                      </>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>

      {editModal.open && (
        <div
          className="modal d-block"
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
          onClick={closeEditModal}
        >
          <div
            className="modal-dialog modal-dialog-centered"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  Editar indicador {editModal.type === "diario" ? "diario" : "mensual"}
                </h5>
                <button type="button" className="btn-close" onClick={closeEditModal} />
              </div>
              <form onSubmit={handleEditSubmit}>
                <div className="modal-body">
                  {editModal.type === "diario" ? (
                    <>
                      <Textfield
                        label="Fecha"
                        type="date"
                        name="date"
                        value={editForm.date}
                        onChange={handleEditChange}
                        error={editErrors.date}
                      />
                      <Textfield
                        label="Excesos de turnos diarios"
                        placeholder="Excesos de turnos diarios"
                        type="number"
                        name="dailyWorkShiftExcesses"
                        value={editForm.dailyWorkShiftExcesses}
                        onChange={handleEditChange}
                        error={editErrors.dailyWorkShiftExcesses}
                      />
                      <Textfield
                        label="Viajes con exceso de velocidad diarios"
                        placeholder="Viajes con exceso de velocidad diarios"
                        type="number"
                        name="dailySpeedingWorkTrips"
                        value={editForm.dailySpeedingWorkTrips}
                        onChange={handleEditChange}
                        error={editErrors.dailySpeedingWorkTrips}
                      />
                    </>
                  ) : (
                    <>
                      <div className="mb-3">
                        <label className="form-label">Mes</label>
                        <input
                          className={`form-control ${editErrors.date ? "is-invalid" : ""}`}
                          type="month"
                          value={editForm.date ? editForm.date.substring(0, 7) : ""}
                          onChange={(e) =>
                            handleEditChange({
                              name: "date",
                              value: `${e.target.value}-01`,
                            })
                          }
                        />
                        {editErrors.date && (
                          <div className="invalid-feedback">{editErrors.date}</div>
                        )}
                      </div>
                      <Textfield
                        label="Días de trabajo total de conductores"
                        placeholder="Días de trabajo total de conductores"
                        type="number"
                        name="totalDriverWorkDays"
                        value={editForm.totalDriverWorkDays}
                        onChange={handleEditChange}
                        error={editErrors.totalDriverWorkDays}
                      />
                      <Textfield
                        label="Total de viajes de trabajo"
                        placeholder="Total de viajes de trabajo"
                        type="number"
                        name="totalWorkTrips"
                        value={editForm.totalWorkTrips}
                        onChange={handleEditChange}
                        error={editErrors.totalWorkTrips}
                      />
                    </>
                  )}
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={closeEditModal}
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={commandLoading}
                  >
                    {commandLoading ? "Guardando..." : "Guardar cambios"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </main>
  );
}

export default ListFunctionalityIndicators;
